import styles from './GlobalAgentActivity.module.less'
import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Button, Table, Tabs, Tag, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
    AgentAccountHealth,
    AgentActivityJob,
    AgentContentJobMode,
    AgentContentJobStatus,
    AgentJobHistoryEntry,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { hashContentPath, unHashContentPath } from 'common/src/ContentPath'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import {
    cancelAgentContentJob,
    getAgentActivity,
    listAgentJobHistory,
} from '@/api/cb/agentContent'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import {
    AgentActivityTab,
    agentActivityTabAtom,
} from '@/atoms/agentActivityTab'
import { setFrontendActiveCourseForContentCreator } from '@/api/cb/contentCreatorAccount'
import { globalIsLoadingAtom } from '@/atoms/globalIsLoading'
import { hasUnsavedChangesSelector } from '@/atoms/unsavedChanges'
import { syncErrorMessage } from '@/utils/syncMessages'
import { AgentActivityNavigationTarget } from './GlobalAgentActivity.types'
import { AgentRunDetailsDrawer } from './AgentRunDetailsDrawer'
import { AgentRunContentTarget } from './AgentRunDetailsDrawer.types'
import { agentActivityAtom } from '@/atoms/agentActivity'
import {
    AGENT_MODE_LABEL,
    AGENT_STATUS_TAG_COLOR,
    getAgentModelLabel,
} from './agentActivityLabels'

const ELAPSED_TICK_MS = 1_000
const HISTORY_PAGE_SIZE = 10
export const AGENT_ACTIVITY_TARGET_STORAGE_KEY = 'agentActivityNavigationTarget'

const jobStatusLabel = (status: AgentContentJobStatus): string =>
    status === AgentContentJobStatus.canceled
        ? 'Stopped'
        : status === AgentContentJobStatus.waitingForLock
          ? 'Waiting for lock'
          : status

const formatElapsed = (elapsedMs: number): string => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1_000))
    const seconds = totalSeconds % 60
    const totalMinutes = Math.floor(totalSeconds / 60)
    if (totalMinutes >= 60) {
        return `${Math.floor(totalMinutes / 60)}:${String(
            totalMinutes % 60
        ).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
    return `${totalMinutes}:${String(seconds).padStart(2, '0')}`
}

const scrollToTarget = (hashedContentPath: string): void => {
    let attempts = 0
    const interval = window.setInterval(() => {
        const target = document.querySelector(
            `[data-agent-content-path="${CSS.escape(hashedContentPath)}"]`
        )
        if (target) {
            window.clearInterval(interval)
            target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } else if (++attempts >= 20) {
            window.clearInterval(interval)
        }
    }, 100)
}

export const GlobalAgentActivity: React.FC<unknown> = (): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesSelector)
    const [viewingState, setViewingState] = useRecoilState(viewingStateAtom)
    const setGlobalIsLoading = useSetRecoilState(globalIsLoadingAtom)
    const [activity, setActivity] = useRecoilState(agentActivityAtom)
    const [activeTab, setActiveTab] = useRecoilState(agentActivityTabAtom)
    const [historyEntries, setHistoryEntries] = useState<
        AgentJobHistoryEntry[]
    >([])
    const [historyPage, setHistoryPage] = useState<number>(1)
    const [historyTotalCount, setHistoryTotalCount] = useState<number>(0)
    const [historyLoading, setHistoryLoading] = useState<boolean>(false)
    const [hasLoadedHistory, setHasLoadedHistory] = useState<boolean>(false)
    const [nowMs, setNowMs] = useState<number>(() => Date.now())
    const [stoppingJobIds, setStoppingJobIds] = useState<Set<string>>(
        () => new Set()
    )
    const selectedJobMetadata = useMemo(
        () =>
            activity?.activeJobs.find((job) => job.jobId === viewingState.id) ??
            historyEntries.find((job) => job.jobId === viewingState.id),
        [activity?.activeJobs, historyEntries, viewingState.id]
    )

    const openDetails = useCallback(
        (jobId: string): void => {
            setViewingState((current) => ({
                ...current,
                id: jobId,
                viewingStateType: ViewingStateType.agentActivity,
            }))
        },
        [setViewingState]
    )

    const closeDetails = useCallback((): void => {
        setViewingState((current) => ({ ...current, id: null }))
    }, [setViewingState])

    const refreshActivity = useCallback(async (): Promise<void> => {
        const response = await getAgentActivity()
        if (response.data.success) setActivity(response.data.payload)
    }, [])

    const refreshHistory = useCallback(async (page: number): Promise<void> => {
        setHistoryLoading(true)
        try {
            const response = await listAgentJobHistory({
                page,
                pageSize: HISTORY_PAGE_SIZE,
            })
            if (response.data.success) {
                setHistoryEntries(response.data.payload.entries)
                setHistoryTotalCount(response.data.payload.totalCount)
                setHistoryPage(page)
                setHasLoadedHistory(true)
            }
        } finally {
            setHistoryLoading(false)
        }
    }, [])

    // Lazy-load history whenever the tab is (or becomes) active — including
    // when another surface opens the modal directly onto History via the atom
    useEffect(() => {
        if (activeTab === 'history' && !hasLoadedHistory) {
            void refreshHistory(1)
        }
    }, [activeTab, hasLoadedHistory, refreshHistory])

    // Reset the tab on unmount so a plain reopen starts on Active
    useEffect(() => {
        return () => setActiveTab('active')
    }, [setActiveTab])

    useEffect(() => {
        setNowMs(Date.now())
        const interval = window.setInterval(
            () => setNowMs(Date.now()),
            ELAPSED_TICK_MS
        )
        return () => window.clearInterval(interval)
    }, [])

    // After a cross-course Open-section reload the URL owns the viewing state
    // (useViewingState restores it); this only replays the scroll hint
    useEffect(() => {
        const storedTarget = sessionStorage.getItem(
            AGENT_ACTIVITY_TARGET_STORAGE_KEY
        )
        if (!storedTarget) return
        const target = JSON.parse(storedTarget) as AgentActivityNavigationTarget
        if (target.courseName !== courseName) return
        sessionStorage.removeItem(AGENT_ACTIVITY_TARGET_STORAGE_KEY)
        scrollToTarget(target.hashedContentPath)
    }, [courseName])

    const goToSection = useCallback(
        async (job: AgentActivityJob): Promise<void> => {
            // Course-wide edits have no single section to open — the drawer
            // hides its navigation button for them; this guard is defensive
            if (job.mode === AgentContentJobMode.courseWideEdit) return
            const hashedContentPath = hashContentPath(job.contentPath)
            // Targeted counterpart jobs land on the card itself (notecard
            // editor, Convert tab renders the generated problem) instead of
            // the section's agent editor.
            const targetNotecardId = job.targetNotecardIds?.[0] ?? null
            const viewingStateType =
                job.mode === AgentContentJobMode.formulaSheet
                    ? ViewingStateType.formulaSheet
                    : job.mode === AgentContentJobMode.previousExamAuthoring
                      ? ViewingStateType.previousExams
                      : job.mode === AgentContentJobMode.sourceMaterialImport
                        ? ViewingStateType.sourceMaterial
                        : targetNotecardId
                          ? ViewingStateType.notecards
                          : ViewingStateType.agentContent
            if (job.courseName === courseName) {
                // Same course: pure state navigation — the tree derives its
                // expansion from viewingState.contentPath and the leaf's agent
                // editor opens on the agentContent viewing type. No reload.
                setViewingState({
                    contentPath:
                        job.mode === AgentContentJobMode.formulaSheet ||
                        job.mode === AgentContentJobMode.previousExamAuthoring
                            ? []
                            : job.contentPath,
                    viewingStateType,
                    id:
                        job.mode === AgentContentJobMode.formulaSheet
                            ? null
                            : targetNotecardId,
                })
                if (
                    job.mode !== AgentContentJobMode.formulaSheet &&
                    job.mode !== AgentContentJobMode.previousExamAuthoring
                ) {
                    scrollToTarget(hashedContentPath)
                }
                return
            }
            if (
                hasUnsavedChanges &&
                !window.confirm(
                    'Changing courses will discard your unsaved changes. Continue?'
                )
            ) {
                return
            }
            setGlobalIsLoading(true)
            const response = await setFrontendActiveCourseForContentCreator(
                job.courseName
            )
            if (response.data.isError) {
                setGlobalIsLoading(false)
                syncErrorMessage(
                    'Unable to change active course. Please refresh and try again.'
                )
                return
            }
            // Course state loads at boot (same constraint as the standard
            // course switcher), so a reload is required — but reload straight
            // INTO the section's deep link so useViewingState restores the
            // expanded tree + open agent editor from the URL. sessionStorage
            // carries only a scroll hint, not state.
            if (
                job.mode !== AgentContentJobMode.formulaSheet &&
                job.mode !== AgentContentJobMode.previousExamAuthoring
            ) {
                sessionStorage.setItem(
                    AGENT_ACTIVITY_TARGET_STORAGE_KEY,
                    JSON.stringify({
                        courseName: job.courseName,
                        hashedContentPath,
                    } as AgentActivityNavigationTarget)
                )
            }
            window.location.assign(
                `${UiLayoutConstant.PRIMARY_DASHBOARD_PAGE}?contentPath=${encodeURIComponent(
                    job.mode === AgentContentJobMode.formulaSheet ||
                        job.mode === AgentContentJobMode.previousExamAuthoring
                        ? ''
                        : hashedContentPath
                )}&viewingStateType=${viewingStateType}${
                    targetNotecardId
                        ? `&id=${encodeURIComponent(targetNotecardId)}`
                        : ''
                }`
            )
        },
        [courseName, hasUnsavedChanges, setGlobalIsLoading, setViewingState]
    )

    // Changes-tab item deep links (same course only — the drawer hides them
    // otherwise). setViewingState pushes a history entry via useViewingState,
    // so the browser Back button returns to this run's drawer
    const openContentItem = useCallback(
        (target: AgentRunContentTarget): void => {
            setViewingState({
                contentPath: unHashContentPath(target.hashedContentPath),
                viewingStateType: target.viewingStateType,
                id: target.id,
            })
            scrollToTarget(target.hashedContentPath)
        },
        [setViewingState]
    )

    const stopJob = useCallback(
        async (jobId: string): Promise<void> => {
            if (!window.confirm('Stop this agent job?')) return
            setStoppingJobIds((ids) => new Set(ids).add(jobId))
            try {
                const response = await cancelAgentContentJob({ jobId })
                if (!response.data.success) {
                    syncErrorMessage(`Unable to stop agent job.`)
                }
                await refreshActivity()
            } finally {
                setStoppingJobIds((ids) => {
                    const next = new Set(ids)
                    next.delete(jobId)
                    return next
                })
            }
        },
        [refreshActivity]
    )

    const columns = useMemo<ColumnsType<AgentActivityJob>>(
        () => [
            {
                title: 'Course',
                dataIndex: 'prettyCourseName',
            },
            {
                title: 'Section',
                render: (_, job) =>
                    job.contentPathTitles.length
                        ? job.contentPathTitles.join(' › ')
                        : job.contentPath.length === 0
                          ? 'Entire course'
                          : '—',
            },
            {
                title: 'Mode',
                render: (_, job) => AGENT_MODE_LABEL[job.mode],
            },
            {
                title: 'Model',
                render: (_, job) => getAgentModelLabel(job.modelName),
            },
            {
                title: 'Started by',
                render: (_, job) => job.requestedByDisplayName ?? 'unknown',
            },
            {
                title: 'Status',
                render: (_, job) => (
                    <div>
                        <Tag color={AGENT_STATUS_TAG_COLOR[job.status]}>
                            {jobStatusLabel(job.status)}
                        </Tag>
                        {job.waitingOn && (
                            <div>
                                waiting for{' '}
                                <Button
                                    type={'link'}
                                    size={'small'}
                                    onClick={() =>
                                        openDetails(job.waitingOn!.jobId)
                                    }
                                >
                                    {AGENT_MODE_LABEL[job.waitingOn.mode]} on{' '}
                                    {job.waitingOn.contentPathTitles.join(
                                        ' › '
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                ),
            },
            {
                title: 'Elapsed',
                render: (_, job) => {
                    if (job.status === AgentContentJobStatus.waitingForLock) {
                        return 'Waiting for another run'
                    }
                    const start = job.startedAt ?? job.createdAt
                    const elapsed = formatElapsed(
                        nowMs - new Date(start).getTime()
                    )
                    return job.startedAt ? elapsed : `${elapsed} queued`
                },
            },
            {
                title: 'Actions',
                render: (_, job) => (
                    <div className={styles.actions}>
                        <Button
                            danger={true}
                            size={'small'}
                            loading={stoppingJobIds.has(job.jobId)}
                            disabled={
                                stoppingJobIds.has(job.jobId) ||
                                (isReadOnly && job.courseName === courseName)
                            }
                            onClick={() => void stopJob(job.jobId)}
                        >
                            Stop
                        </Button>
                        <Button
                            size={'small'}
                            onClick={() => openDetails(job.jobId)}
                        >
                            Details
                        </Button>
                    </div>
                ),
            },
        ],
        [courseName, isReadOnly, nowMs, openDetails, stopJob, stoppingJobIds]
    )

    const historyColumns = useMemo<ColumnsType<AgentJobHistoryEntry>>(
        () => [
            {
                title: 'Course',
                dataIndex: 'prettyCourseName',
            },
            {
                title: 'Section',
                render: (_, job) =>
                    job.contentPathTitles.length
                        ? job.contentPathTitles.join(' › ')
                        : job.contentPath.length === 0
                          ? 'Entire course'
                          : '—',
            },
            {
                title: 'Mode',
                render: (_, job) => AGENT_MODE_LABEL[job.mode],
            },
            {
                title: 'Model',
                render: (_, job) => getAgentModelLabel(job.modelName),
            },
            {
                title: 'Started by',
                render: (_, job) => job.requestedByDisplayName ?? 'unknown',
            },
            {
                title: 'Request',
                render: (_, job) =>
                    job.instructorRequest ? (
                        <Tooltip title={job.instructorRequest}>
                            <span className={styles.requestSnippet}>
                                {job.instructorRequest.length > 60
                                    ? `${job.instructorRequest.slice(0, 60)}…`
                                    : job.instructorRequest}
                            </span>
                        </Tooltip>
                    ) : (
                        '—'
                    ),
            },
            {
                title: 'Status',
                render: (_, job) => {
                    const tag = (
                        <Tag color={AGENT_STATUS_TAG_COLOR[job.status]}>
                            {jobStatusLabel(job.status)}
                        </Tag>
                    )
                    return job.status === AgentContentJobStatus.failed &&
                        job.errorSnippet ? (
                        <Tooltip title={job.errorSnippet}>{tag}</Tooltip>
                    ) : (
                        tag
                    )
                },
            },
            {
                title: 'Started',
                render: (_, job) =>
                    new Date(job.startedAt ?? job.createdAt).toLocaleString(),
            },
            {
                title: 'Duration',
                render: (_, job) =>
                    job.startedAt && job.endedAt
                        ? formatElapsed(
                              new Date(job.endedAt).getTime() -
                                  new Date(job.startedAt).getTime()
                          )
                        : '—',
            },
            {
                title: 'Actions',
                render: (_, job) => (
                    <div className={styles.actions}>
                        {(job.status === AgentContentJobStatus.queued ||
                            job.status ===
                                AgentContentJobStatus.waitingForLock ||
                            job.status === AgentContentJobStatus.running) && (
                            <Button
                                danger={true}
                                size={'small'}
                                loading={stoppingJobIds.has(job.jobId)}
                                disabled={
                                    stoppingJobIds.has(job.jobId) ||
                                    (isReadOnly &&
                                        job.courseName === courseName)
                                }
                                onClick={() => void stopJob(job.jobId)}
                            >
                                Stop
                            </Button>
                        )}
                        <Button
                            size={'small'}
                            onClick={() => openDetails(job.jobId)}
                        >
                            Details
                        </Button>
                    </div>
                ),
            },
        ],
        [courseName, isReadOnly, openDetails, stopJob, stoppingJobIds]
    )

    const unhealthyAccounts =
        activity?.accounts.filter(
            (account) => account.health !== AgentAccountHealth.healthy
        ) ?? []
    return (
        <>
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key as AgentActivityTab)
                }}
                items={[
                    {
                        key: 'active',
                        label: 'Active',
                        children: (
                            <>
                                <div className={styles.modalHeader}>
                                    Agents:{' '}
                                    {activity?.instructorPool.running ?? 0} of{' '}
                                    {activity?.instructorPool.cap ?? 0} running
                                    · {activity?.instructorPool.queued ?? 0}{' '}
                                    queued
                                </div>
                                {unhealthyAccounts.length > 0 && (
                                    <div className={styles.accountWarning}>
                                        Account warning:{' '}
                                        {unhealthyAccounts.map(
                                            (account, index) => (
                                                <React.Fragment
                                                    key={account.name}
                                                >
                                                    {index > 0 ? ', ' : ''}
                                                    <span
                                                        title={
                                                            account.coolingDownUntil ??
                                                            undefined
                                                        }
                                                    >
                                                        {account.name} (
                                                        {account.health})
                                                    </span>
                                                </React.Fragment>
                                            )
                                        )}
                                    </div>
                                )}
                                <Table<AgentActivityJob>
                                    size={'small'}
                                    rowKey={'jobId'}
                                    columns={columns}
                                    dataSource={activity?.activeJobs ?? []}
                                    pagination={false}
                                    scroll={{ x: true }}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'history',
                        label: 'History',
                        children: (
                            <Table<AgentJobHistoryEntry>
                                size={'small'}
                                rowKey={'jobId'}
                                columns={historyColumns}
                                dataSource={historyEntries}
                                loading={historyLoading}
                                pagination={{
                                    current: historyPage,
                                    pageSize: HISTORY_PAGE_SIZE,
                                    total: historyTotalCount,
                                    onChange: (page) =>
                                        void refreshHistory(page),
                                }}
                                scroll={{ x: true }}
                            />
                        ),
                    },
                ]}
            />
            <AgentRunDetailsDrawer
                jobId={
                    viewingState.viewingStateType ===
                        ViewingStateType.agentActivity && viewingState.id
                        ? viewingState.id
                        : null
                }
                jobMetadata={selectedJobMetadata}
                displayedCourseName={courseName}
                isDisplayedCourseReadOnly={isReadOnly}
                stopping={
                    viewingState.id !== null &&
                    stoppingJobIds.has(viewingState.id)
                }
                onClose={closeDetails}
                onOpenContentItem={openContentItem}
                onOpenSection={goToSection}
                onStop={stopJob}
            />
        </>
    )
}
